import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { DiaryManagementNavTabs } from "@/features/admin/components/DiaryManagementNavTabs";
import type { AdminEntryFilters, EntryStatus } from "@/features/admin/entries/api/adminEntriesApi";
import { ENTRY_STATUSES, useAdminEntries } from "@/features/admin/entries/api/adminEntriesApi";
import { ADMIN_ENTRIES_COLUMNS, AdminEntriesTable } from "@/features/admin/entries/components/AdminEntriesTable";
import type { AdminEntriesFilterValues } from "@/features/admin/entries/components/AdminEntriesToolbar";
import {
    emptyAdminEntriesFilterValues,
    AdminEntriesToolbar,
} from "@/features/admin/entries/components/AdminEntriesToolbar";
import {
    ADMIN_TABLE_PAGE_SIZE,
    AdminColumnsDialog,
    AdminTablePagination,
    useDebouncedValue,
} from "@/features/admin/tables";
import { useNav } from "@/utils/nav";
import { ui } from "@patient-watch/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

const EntriesSearchZ = z.object({
    q: z.string().optional(),
    page: z.coerce.number().int().min(1).optional(),
    diaryId: z.string().optional(),
    trackerId: z.string().optional(),
    entryId: z.string().optional(),
    organisationId: z.string().optional(),
    status: z.enum(ENTRY_STATUSES).optional(),
});

export const Route = createFileRoute("/_app/admin/entries")({
    component: AdminEntriesPage,
    validateSearch: (search) => EntriesSearchZ.parse(search),
});

function AdminEntriesPage() {
    useNav([
        { label: "Admin", route: { to: "/admin" } },
        { label: "Entries", route: { to: "/admin/entries" } },
    ]);

    const search = Route.useSearch();
    const navigate = Route.useNavigate();

    const [searchText, setSearchText] = useState(search.q ?? "");
    const debouncedSearch = useDebouncedValue(searchText.trim(), 300);

    const [filterValues, setFilterValues] = useState<AdminEntriesFilterValues>(() => ({
        ...emptyAdminEntriesFilterValues,
        diaryId: search.diaryId ?? null,
        trackerId: search.trackerId ?? null,
        entryId: search.entryId ?? null,
        organisationId: search.organisationId ?? null,
        status: search.status ?? "all",
    }));

    const [visibleKeys, setVisibleKeys] = useState(() =>
        ADMIN_ENTRIES_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)
    );

    const page = search.page ?? 1;

    useEffect(() => {
        const urlQ = search.q ?? "";
        if (debouncedSearch === urlQ) return;
        void navigate({
            search: (prev) => ({
                ...prev,
                q: debouncedSearch || undefined,
                page: 1,
            }),
            replace: true,
        });
    }, [debouncedSearch, navigate, search.q]);

    useEffect(() => {
        setFilterValues((prev) => ({
            ...prev,
            diaryId: search.diaryId ?? null,
            trackerId: search.trackerId ?? null,
            entryId: search.entryId ?? null,
            organisationId: search.organisationId ?? null,
            status: search.status ?? "all",
        }));
    }, [search.diaryId, search.trackerId, search.entryId, search.organisationId, search.status]);

    const filters: AdminEntryFilters = useMemo(
        () => ({
            q: debouncedSearch || undefined,
            diaryId: filterValues.diaryId ?? undefined,
            trackerId: filterValues.trackerId ?? undefined,
            entryId: filterValues.entryId ?? undefined,
            organisationId: filterValues.organisationId ?? undefined,
            status: filterValues.status === "all" ? undefined : (filterValues.status as EntryStatus),
            page,
            pageSize: ADMIN_TABLE_PAGE_SIZE,
        }),
        [debouncedSearch, filterValues, page]
    );

    const { data, isLoading, isFetching, error } = useAdminEntries(filters);
    const entries = data?.entries ?? [];

    return (
        <AdminPageLayout
            title="Entries"
            description="Browse questionnaire entries across diaries. Filter by diary, tracker, organisation, or status."
        >
            <div className="space-y-4">
                <DiaryManagementNavTabs />

                <AdminEntriesToolbar
                    searchText={searchText}
                    onSearchTextChange={setSearchText}
                    filterValues={filterValues}
                    onApplyFilters={(values) => {
                        setFilterValues(values);
                        void navigate({
                            search: (prev) => ({
                                ...prev,
                                page: 1,
                                diaryId: values.diaryId ?? undefined,
                                trackerId: values.trackerId ?? undefined,
                                entryId: values.entryId ?? undefined,
                                organisationId: values.organisationId ?? undefined,
                                status: values.status === "all" ? undefined : values.status,
                            }),
                            replace: true,
                        });
                    }}
                    isApplying={isFetching}
                >
                    <AdminColumnsDialog
                        columns={ADMIN_ENTRIES_COLUMNS}
                        visibleKeys={visibleKeys}
                        onSave={setVisibleKeys}
                    />
                </AdminEntriesToolbar>

                {filterValues.entryId || filterValues.diaryId || filterValues.trackerId ? (
                    <p className="text-muted-foreground text-xs">
                        {filterValues.entryId ? (
                            <>
                                Filtered to entry <span className="font-mono">{filterValues.entryId}</span>
                            </>
                        ) : null}
                        {filterValues.diaryId ? (
                            <>
                                {filterValues.entryId ? " · " : null}
                                Filtered to diary <span className="font-mono">{filterValues.diaryId}</span>
                            </>
                        ) : null}
                        {filterValues.trackerId ? (
                            <>
                                {filterValues.entryId || filterValues.diaryId ? " · " : null}
                                Tracker <span className="font-mono">{filterValues.trackerId}</span>
                            </>
                        ) : null}
                        {" · "}
                        <button
                            type="button"
                            className="text-sky-700 underline"
                            onClick={() => {
                                const cleared = {
                                    ...filterValues,
                                    entryId: null,
                                    diaryId: null,
                                    trackerId: null,
                                };
                                setFilterValues(cleared);
                                void navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        entryId: undefined,
                                        diaryId: undefined,
                                        trackerId: undefined,
                                        page: 1,
                                    }),
                                    replace: true,
                                });
                            }}
                        >
                            Clear ID filters
                        </button>
                    </p>
                ) : null}

                {error && (
                    <div className="bg-red-50 p-4 border border-red-200 rounded-md text-red-800 text-sm">
                        Error loading entries: {error instanceof Error ? error.message : "Unknown error"}
                    </div>
                )}

                {isLoading ? (
                    <ui.Skeleton className="w-full h-64" />
                ) : (
                    !error && (
                        <div className={isFetching ? "opacity-70 transition-opacity" : undefined}>
                            <AdminEntriesTable entries={entries} visibleKeys={visibleKeys} />
                            <div className="mt-3">
                                <AdminTablePagination
                                    page={page}
                                    totalCount={data?.totalCount ?? 0}
                                    pageSize={ADMIN_TABLE_PAGE_SIZE}
                                    isFetching={isFetching}
                                    onPageChange={(nextPage) => {
                                        void navigate({
                                            search: (prev) => ({ ...prev, page: nextPage }),
                                            replace: true,
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    )
                )}
            </div>
        </AdminPageLayout>
    );
}
